import { useCallback, useEffect, useRef, useState } from 'react';
import { getCaseTemplateAsync } from '../modules/Templates/template.api';

/**
 * Status of an in-progress (or finished) template generation poll.
 *
 * - idle: polling has not been started (or was cancelled/reset)
 * - polling: waiting for the case-template combo to finish generating
 * - completed: the generated document id was observed
 * - timedOut: the maximum wait elapsed without observing a generated document id
 * - failed: the status-check endpoint errored on enough consecutive attempts that
 *   we gave up rather than waiting out the full timeout
 */
export type TemplateGenerationPollStatus = 'idle' | 'polling' | 'completed' | 'timedOut' | 'failed';

export interface IUseTemplateGenerationPollingOptions {
	/** How often to check for completion, in milliseconds. Defaults to 3000 (3s). */
	intervalMs?: number;
	/** The maximum total time to wait before giving up, in milliseconds. Defaults to 300000 (5 minutes). */
	timeoutMs?: number;
	/** The number of consecutive failed status checks that will abort polling early. Defaults to 3. */
	maxConsecutiveErrors?: number;
	/**
	 * Overrides the default case-template completion check, allowing this hook to be reused for other
	 * generation-status polling scenarios (for example, standalone Template Maintenance regeneration,
	 * which has no caseId). Receives the id passed to startPolling and should resolve true once the
	 * generation has finished.
	 */
	checkCompletion?: (id: number) => Promise<boolean>;
	/** Called once when the case-template combo is observed to have finished generating. */
	onCompleted?: (caseId: number) => void;
	/** Called once if the maximum wait elapses without the generation completing. */
	onTimedOut?: (caseId: number) => void;
	/** Called once if the status-check endpoint fails on maxConsecutiveErrors consecutive attempts. */
	onError?: (caseId: number, error: unknown) => void;
}

export interface IUseTemplateGenerationPollingResult {
	/** Returns the current poll status for the given id (idle if it has never been started). */
	getStatus: (id: number) => TemplateGenerationPollStatus;
	/** Returns true if the given id currently has an in-flight poll. */
	isPolling: (id: number) => boolean;
	/** Begins polling GetCaseTemplate for the given id until it has a non-zero templateDocumentId.
	 *  Multiple ids can be polled concurrently and independently of one another. */
	startPolling: (id: number) => void;
	/** Stops polling the given id without invoking onCompleted or onTimedOut. */
	cancelPolling: (id: number) => void;
}

const DEFAULT_INTERVAL_MS = 3000;
const DEFAULT_TIMEOUT_MS = 300000;
const DEFAULT_MAX_CONSECUTIVE_ERRORS = 3;

interface IPollEntry {
	intervalId: ReturnType<typeof setInterval>;
	attempt: number;
	consecutiveErrors: number;
	requestInFlight: boolean;
}

/**
 * Reusable hook that polls `GET api/Template/GetCaseTemplate/{caseId}` (via getCaseTemplateAsync)
 * until the case-template combo has finished (re)generating, signalled by a non-zero
 * `templateDocumentId`. There is no dedicated generation-status column to poll against
 * (see plan.md for the investigation), so this is the same completion signal already trusted
 * by CaseDetailForm.downloadTemplate().
 *
 * The hook is intentionally generic (keyed only by a numeric id, e.g. caseId or templateId) so it
 * can be reused anywhere a template regeneration needs to be awaited, not just the Case Details
 * page. Because a single hook instance tracks every id independently (its own interval, attempt
 * count, and error streak), callers such as the Template List grid can trigger and track
 * generation for several rows at the same time without one row's poll interfering with another's.
 * Callers that aren't polling a case-template combo (for example standalone Template Maintenance)
 * can supply a `checkCompletion` override to replace the default case-template check.
 */
export function useTemplateGenerationPolling(
	options?: IUseTemplateGenerationPollingOptions
): IUseTemplateGenerationPollingResult {
	const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;
	const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
	const maxAttempts = Math.max(1, Math.ceil(timeoutMs / intervalMs));
	const maxConsecutiveErrors = options?.maxConsecutiveErrors ?? DEFAULT_MAX_CONSECUTIVE_ERRORS;

	const [statuses, setStatuses] = useState<Record<number, TemplateGenerationPollStatus>>({});

	// Keep the latest callbacks in refs so changing them doesn't restart any in-progress poll.
	const onCompletedRef = useRef(options?.onCompleted);
	const onTimedOutRef = useRef(options?.onTimedOut);
	const onErrorRef = useRef(options?.onError);
	const checkCompletionRef = useRef(options?.checkCompletion);
	onCompletedRef.current = options?.onCompleted;
	onTimedOutRef.current = options?.onTimedOut;
	onErrorRef.current = options?.onError;
	checkCompletionRef.current = options?.checkCompletion;

	// Independent poll state per id, so concurrent polls for different ids don't clobber each other.
	const entriesRef = useRef<Map<number, IPollEntry>>(new Map());

	const setStatus = useCallback((id: number, newStatus: TemplateGenerationPollStatus) => {
		setStatuses(prev => ({ ...prev, [id]: newStatus }));
	}, []);

	const stopPollingForId = useCallback((id: number) => {
		const entry = entriesRef.current.get(id);
		if (entry) {
			clearInterval(entry.intervalId);
			entriesRef.current.delete(id);
		}
	}, []);

	const cancelPolling = useCallback((id: number) => {
		stopPollingForId(id);
		setStatus(id, 'idle');
	}, [stopPollingForId, setStatus]);

	const startPolling = useCallback((id: number) => {
		// Reset any prior run for this id before starting a new one; other ids' polls are untouched.
		stopPollingForId(id);
		setStatus(id, 'polling');

		const poll = () => {
			const entry = entriesRef.current.get(id);
			if (!entry) {
				return;
			}

			// Guard against overlapping requests if a poll takes longer than the interval.
			if (entry.requestInFlight) {
				return;
			}

			entry.attempt += 1;
			entry.requestInFlight = true;

			const checkCompletion = checkCompletionRef.current
				?? (async (checkId: number) => {
					const data = await getCaseTemplateAsync(checkId);
					return !!(data && data.templateDocumentId);
				});

			checkCompletion(id)
				.then(isComplete => {
					if (isComplete) {
						stopPollingForId(id);
						setStatus(id, 'completed');
						onCompletedRef.current?.(id);
						return;
					}

					// A successful (if not-yet-complete) status check clears any prior error streak.
					entry.consecutiveErrors = 0;

					if (entry.attempt >= maxAttempts) {
						stopPollingForId(id);
						setStatus(id, 'timedOut');
						onTimedOutRef.current?.(id);
					}
				})
				.catch(error => {
					// A transient failure on a single poll shouldn't abort the whole wait,
					// but enough consecutive failures means the status check itself is broken.
					console.error('Error while polling for template generation completion:', error);
					entry.consecutiveErrors += 1;

					if (entry.consecutiveErrors >= maxConsecutiveErrors) {
						stopPollingForId(id);
						setStatus(id, 'failed');
						onErrorRef.current?.(id, error);
						return;
					}

					if (entry.attempt >= maxAttempts) {
						stopPollingForId(id);
						setStatus(id, 'timedOut');
						onTimedOutRef.current?.(id);
					}
				})
				.finally(() => {
					entry.requestInFlight = false;
				});
		};

		const intervalId = setInterval(poll, intervalMs);
		entriesRef.current.set(id, {
			intervalId,
			attempt: 0,
			consecutiveErrors: 0,
			requestInFlight: false,
		});
		poll();
	}, [intervalMs, maxAttempts, maxConsecutiveErrors, stopPollingForId, setStatus]);

	const getStatus = useCallback((id: number): TemplateGenerationPollStatus => {
		return statuses[id] ?? 'idle';
	}, [statuses]);

	const isPolling = useCallback((id: number): boolean => {
		return (statuses[id] ?? 'idle') === 'polling';
	}, [statuses]);

	useEffect(() => {
		const entries = entriesRef.current;
		return () => {
			entries.forEach(entry => clearInterval(entry.intervalId));
			entries.clear();
		};
	}, []);

	return {
		getStatus,
		isPolling,
		startPolling,
		cancelPolling,
	};
}

export default useTemplateGenerationPolling;
